import { useRef, useState, useMemo, useEffect } from "react";
import { RecommendedFillersPopover } from "./RecommendedFillersPopover";
import { recommendFillerCarpetJobs } from "../../utility/recommendFillerCarpetJobs";
import { recommendFillerHardSurfaceJobs } from "../../utility/recommendFillerHardSurfaceJobs";
import { mergeFillerRecommendations } from "../../utility/mergeFillerRecommendations";
import { formatYds } from "../../utility/installerCapacityRemaining";

// #178 — document-level event so only ONE Capacity Recommendations popover is
// open at a time. Each icon stops pointer/click propagation (to protect the
// cell's drag-drop), which also prevents an already-open popover's outside-
// click handler from seeing a click on a DIFFERENT icon — so without
// coordination every clicked icon stayed open. When an icon opens it
// broadcasts this event with its own id; every other open icon closes itself.
// Works across all grids (document-level), no parent prop-drilling.
const POPOVER_OPEN_EVENT = "scheduler:partial-capacity-popover-open";

// - - - - - - - - - -
// #175 Option B — run the carpet recommender for a given carpet capacityInfo.
const runCarpetRecommender = ({
  cellInstallerCandidate,
  cellDate,
  info,
  activeQueueDocs,
  furnitureSkuList,
  stepsSkuList,
  normalizePayroll,
  laborAttributeBySku,
  isOverCapacity,
}) =>
  recommendFillerCarpetJobs({
    cellInstallerCandidate,
    cellDate,
    remainingYds: info?.remaining,
    remainingFurniture: info?.remainingFurniture,
    remainingSteps: info?.remainingSteps,
    remainingJobStops: info?.remainingJobStops,
    remainingRepairStops: info?.remainingRepairStops,
    activeQueueDocs,
    furnitureSkuList,
    stepsSkuList,
    normalizePayroll,
    laborAttributeBySku,
    isOverCapacity,
  });

// #175 Option B — run the hard-surface recommender for a given hard capacityInfo.
const runHardRecommender = ({
  cellInstallerCandidate,
  cellDate,
  info,
  activeQueueDocs,
  normalizePayroll,
  laborAttributeBySku,
  isOverCapacity,
}) =>
  recommendFillerHardSurfaceJobs({
    cellInstallerCandidate,
    cellDate,
    perType: info?.perType,
    remainingJobStops: info?.remainingJobStops,
    remainingRepairStops: info?.remainingRepairStops,
    activeQueueDocs,
    normalizePayroll,
    laborAttributeBySku,
    isOverCapacity,
  });

// - - - - - - - - - -
// Cell-corner icon for issue #140 — clickable indicator that an installer
// has remaining carpet capacity on this day, with the popover hung off
// the icon's bounding rect.
//
// Owns its own popover open-state so the OptimizerGrid integration is a
// single render call per cell. Multiple icons across the grid each manage
// their own popover; outside-click on the document body closes whichever
// one is open (because each popover registers a global mousedown listener
// only while open). Opening a second icon implicitly closes the first via
// that outside-click handler — natural single-popover-at-a-time behavior
// without parent coordination.
//
// The recommender query runs LAZILY when the icon is clicked, not at render
// time. With many partial-capacity cells across the grid, running the query
// up-front would be wasted work — most cells are never clicked. Running on
// click is cheap (the active-queue docs are already in memory) and avoids
// the per-render overhead.

export const PartialCapacityIcon = ({
  capacityInfo,
  cellInstallerCandidate,
  cellDate,
  installerName,
  activeQueueDocs,
  furnitureSkuList,
  stepsSkuList,
  normalizePayroll,
  laborAttributeBySku,
}) => {
  const buttonRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);

  // #178 — stable per-instance id so an icon can ignore its own open broadcast.
  const instanceIdRef = useRef(null);
  if (instanceIdRef.current === null) {
    instanceIdRef.current = Symbol("partialCapacityIcon");
  }

  // #178 — close this popover whenever a DIFFERENT icon opens one.
  useEffect(() => {
    const onAnotherOpened = (e) => {
      if (e.detail !== instanceIdRef.current) setOpen(false);
    };
    document.addEventListener(POPOVER_OPEN_EVENT, onAnotherOpened);
    return () =>
      document.removeEventListener(POPOVER_OPEN_EVENT, onAnotherOpened);
  }, []);

  // Lazy-compute recommendations once on open, then memoize until the
  // active-queue data changes (polling refresh) or the cell-context shifts.
  // useMemo with `open` in deps so the popover doesn't keep recomputing on
  // every parent re-render while it's just sitting open.
  const recommendations = useMemo(() => {
    if (!open) return null;
    // #175 Option B — "does-both" installer: run BOTH recommenders against
    // their respective sub-infos and merge the per-tier lists.
    if (capacityInfo?.isBothSurfaces) {
      const carpetRecs = runCarpetRecommender({
        cellInstallerCandidate,
        cellDate,
        info: capacityInfo.carpetInfo,
        activeQueueDocs,
        furnitureSkuList,
        stepsSkuList,
        normalizePayroll,
        laborAttributeBySku,
        isOverCapacity: capacityInfo.isOverCapacity,
      });
      const hardRecs = runHardRecommender({
        cellInstallerCandidate,
        cellDate,
        info: capacityInfo.hardInfo,
        activeQueueDocs,
        normalizePayroll,
        laborAttributeBySku,
        isOverCapacity: capacityInfo.isOverCapacity,
      });
      return mergeFillerRecommendations(carpetRecs, hardRecs);
    }
    // #173 — route to the hard-surface recommender when the cell's capacity
    // came from the hard-surface engine (it carries `isHardSurface` + the
    // per-type breakdown); otherwise the carpet recommender.
    if (capacityInfo?.isHardSurface) {
      return runHardRecommender({
        cellInstallerCandidate,
        cellDate,
        info: capacityInfo,
        activeQueueDocs,
        normalizePayroll,
        laborAttributeBySku,
        isOverCapacity: capacityInfo.isOverCapacity,
      });
    }
    return runCarpetRecommender({
      cellInstallerCandidate,
      cellDate,
      info: capacityInfo,
      activeQueueDocs,
      furnitureSkuList,
      stepsSkuList,
      normalizePayroll,
      laborAttributeBySku,
      isOverCapacity: capacityInfo?.isOverCapacity,
    });
  }, [
    open,
    cellInstallerCandidate,
    cellDate,
    capacityInfo,
    activeQueueDocs,
    furnitureSkuList,
    stepsSkuList,
    normalizePayroll,
    laborAttributeBySku,
  ]);

  const handleClick = (e) => {
    // Prevent the cell's drag-drop pointer-down handler from firing.
    e.stopPropagation();
    e.preventDefault();
    const willOpen = !open;
    if (willOpen) {
      const rect = buttonRef.current?.getBoundingClientRect();
      setAnchorRect(rect || null);
      // #178 — tell every other open popover to close before we open ours.
      document.dispatchEvent(
        new CustomEvent(POPOVER_OPEN_EVENT, {
          detail: instanceIdRef.current,
        })
      );
    }
    setOpen(willOpen);
  };

  const handleClose = () => setOpen(false);

  // Title text — provides at-a-glance capacity info on hover even before
  // opening the popover. Helps install managers triage which cells are
  // worth investigating.
  //
  // #157 (2026-05-22): "remaining" is now the next-job cap (per-job + extras-
  // budget model). Under the old daily-budget model the title said "X yds
  // remaining of Y"; under the new model that's redundant (remaining == cap
  // when in single-load mode). Phrasing tightened to "Next job: up to X yds".
  //
  // #155 (2026-05-22): icon also fires on the #138 next-day-empty cells, so
  // the title needs to read sensibly for both cases (carpetJobCount=0 means
  // the cell is empty; >=1 means partial). Same copy works either way.
  // #173 — unit-aware. Carpet shows a single next-job cap in yds; hard surface
  // has per-type capacity, so the tooltip just signals there's room (the
  // popover lists the per-type detail).
  const titleText = !capacityInfo
    ? "Click for recommendations"
    : capacityInfo.isBothSurfaces || capacityInfo.isHardSurface
    ? "Capacity Recommendations — room on this day. Click for recommendations."
    : `Capacity Recommendations — next job up to ${formatYds(capacityInfo.remaining)} yds. Click for recommendations.`;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleClick}
        // Prevent drag-drop on the parent cell from being canceled by the
        // icon's pointer events. The cell's onDragOver/onDrop should keep
        // working — we only intercept the click.
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        title={titleText}
        aria-label={titleText}
        data-testid="partial-capacity-icon"
        style={{
          // #164 (Brandon, 2026-05-28): icon is now rendered INSIDE the
          // bottom flex row alongside `renderSelectedSurfaceValues` (see
          // OptimizerGrid). No more absolute positioning — the icon and
          // surface totals are siblings in the same `flex items-end
          // justify-end gap-2` container, so they sit side-by-side at the
          // bottom-right of the cell instead of overlapping.
          // Earlier iterations of this fix (top-right → bottom-LEFT →
          // bottom-right via absolute) all had collision issues: top-right
          // covered per-job yardage on the JobTile (Brandon's original
          // complaint); bottom-LEFT still risked covering tile content on
          // certain layouts; bottom-RIGHT absolute overlapped the surface
          // totals to the point of unreadability (Leland's review).
          // flexShrink: 0 protects the 22px circle from being squeezed
          // when surface totals are long.
          flexShrink: 0,
          width: "22px",
          height: "22px",
          borderRadius: "50%",
          border: "1.5px solid #dc2626",
          backgroundColor: "rgba(255, 255, 255, 0.92)",
          color: "#dc2626",
          fontSize: "0.75rem",
          lineHeight: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          padding: 0,
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.25)",
        }}
      >
        🔍
      </button>
      <RecommendedFillersPopover
        open={open}
        anchorRect={anchorRect}
        onClose={handleClose}
        installerName={installerName}
        cellDate={cellDate}
        capacityInfo={capacityInfo}
        recommendations={recommendations}
      />
    </>
  );
};

export default PartialCapacityIcon;
